import { type ReactNode, type Ref, useMemo } from 'react';

import { useBreakpoint } from '@/hooks/useBreakpoint';
import { type DateRange } from '@/modules/analytics/dateSelection';
import { type Period } from '@/modules/analytics/types';
import { getSelectedPeriod } from '@/modules/analytics/utils';

import { DateRangeCompactDropdown } from './DateRangeCompactDropdown';
import { DateRangePopover } from './DateRangePopover';

export interface DateRangeMenuProps {
  // `yyyy-MM-dd`
  startDate: string;
  endDate: string;
  onPresetSelect: (period: Period) => void;
  onRangeSelect: (range: DateRange) => void;
  isPresetDisabled?: (period: Period) => boolean;
  isDayDisabled?: (day: Date) => boolean;
  retentionStart?: Date | null;
  today?: Date;
  align?: 'start' | 'end';
  footer?: ReactNode;
  onRequestClose?: () => void;
  selectedPeriod?: Period | null;
  ref?: Ref<HTMLDivElement>;
}

export type DateRangeLayoutProps = Omit<DateRangeMenuProps, 'selectedPeriod' | 'ref'> & {
  selectedPeriod: Period | null;
};

export const DateRangeMenu = ({ ref, selectedPeriod, ...props }: DateRangeMenuProps) => {
  const isCompact = useBreakpoint('sm');
  const derived = useMemo(
    () => getSelectedPeriod(props.startDate, props.endDate),
    [props.startDate, props.endDate]
  );
  const selected = selectedPeriod !== undefined ? selectedPeriod : derived;

  if (isCompact) {
    return <DateRangeCompactDropdown {...props} ref={ref} selectedPeriod={selected} />;
  }
  return <DateRangePopover {...props} selectedPeriod={selected} />;
};
